import React from 'react';
import { PlusIcon } from '@heroicons/react/24/outline';
import { isAssignedOwnerName } from '../../utils/nameNormalization';
import { resolveCanonicalOwnerUid } from '../../utils/ownerIdentity';
import UserAvatar from './UserAvatar';

interface OwnerListAvatarProps {
  name?: string | null;
  colorSeed?: string | null;
  className?: string;
}

/** Fixed-size Owner column trigger so assigned/unassigned does not jump the table. */
export const OWNER_COLUMN_TRIGGER_CLASS =
  'relative inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full group/owner-col focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-400 disabled:cursor-not-allowed';

export const OWNER_COLUMN_EMPTY_TITLE = 'Assign owner';

/** Grey 24px plus — shared unassigned mark (same size as UserAvatar xs). */
export const OwnerUnassignedMark: React.FC<{ className?: string }> = ({ className = '' }) => (
  <span
    className={`inline-flex h-6 w-6 items-center justify-center rounded-full bg-neutral-100 text-neutral-400 ring-1 ring-inset ring-neutral-200 group-hover/owner-col:bg-neutral-200/80 group-hover/owner-col:text-neutral-500 ${className}`.trim()}
    aria-hidden
  >
    <PlusIcon className="h-3 w-3" strokeWidth={2} />
  </span>
);

/** List-row owner circle: xs, no ring, hashed colour. Unassigned renders nothing. */
const OwnerListAvatar: React.FC<OwnerListAvatarProps> = ({ name, colorSeed, className = '' }) => {
  if (!isAssignedOwnerName(name)) return null;
  const displayName = name!.trim();
  const seed = resolveCanonicalOwnerUid(colorSeed, displayName) || displayName;
  return (
    <UserAvatar
      user={{ displayName }}
      size="xs"
      showBorder={false}
      colorSeed={seed || undefined}
      className={`flex-shrink-0 ${className}`.trim()}
    />
  );
};

/** Idle Owner column: initials circle or plus mark. Hover title lives on the cell trigger. */
export const OwnerColumnIdle: React.FC<{
  name?: string | null;
  colorSeed?: string | null;
}> = ({ name, colorSeed }) =>
  isAssignedOwnerName(name) ? (
    <OwnerListAvatar name={name} colorSeed={colorSeed} />
  ) : (
    <OwnerUnassignedMark />
  );

/** Avatar beside the full name in the owner picker menu (not the column). */
export const ownerMenuLeading = (
  name?: string | null,
  colorSeed?: string | null
): React.ReactNode =>
  isAssignedOwnerName(name) ? <OwnerListAvatar name={name} colorSeed={colorSeed || name} /> : undefined;

export default OwnerListAvatar;
